import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChangeEvent, DragEvent, useEffect, useMemo, useState } from 'react';
import { BETA_API_PATH, FEATURED_COLLECTION_ID, PROD_API_PATH } from '../constants';
import { isProd } from '../utils';

type CategoryOption = 'Reflections' | 'Life' | 'Projects' | 'Travel' | 'Cities' | 'Nature';
type SubmitTarget = 'beta' | 'prod';
const CATEGORY_OPTIONS: CategoryOption[] = ['Reflections', 'Life', 'Projects', 'Travel', 'Cities', 'Nature'];

interface PendingPhoto {
    localId: string;
    source: 'new' | 'existing';
    file?: File;
    fileName: string;
    existingMediaKey?: string;
    caption: string;
    previewUrl: string;
    focalPointX: number;
    focalPointY: number;
    squareFocalPointX: number;
    squareFocalPointY: number;
}

interface ExistingCollectionImage {
    id: string;
    mediaKey: string;
    imageUrl: string;
    caption: string;
    sortOrder: number;
    focalPointX?: number;
    focalPointY?: number;
    squareFocalPointX?: number;
    squareFocalPointY?: number;
}

interface ExistingCollectionResponse {
    id: string;
    title: string;
    category: CategoryOption;
    location: string;
    description: string;
    publishedAt: number;
    galleryImages: ExistingCollectionImage[];
}

const MAX_ESTIMATED_REQUEST_BYTES = 9_500_000;
const FEATURED_COLLECTION_DEFAULT_TITLE = 'Featured Photos';
const FEATURED_COLLECTION_DEFAULT_CATEGORY: CategoryOption = 'Life';
const FEATURED_COLLECTION_DEFAULT_LOCATION = '';
interface UploadPhotoPayload {
    fileName?: string;
    contentType?: string;
    base64Data?: string;
    mediaKey?: string;
    caption: string;
    sortOrder: number;
    focalPointX: number;
    focalPointY: number;
    squareFocalPointX?: number;
    squareFocalPointY?: number;
}

function toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function PhotoCollectionBuilder() {
    const [title, setTitle] = useState('');
    const [collectionId, setCollectionId] = useState('');
    const [targetFeaturedCollection, setTargetFeaturedCollection] = useState(false);
    const [editExistingCollection, setEditExistingCollection] = useState(false);
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState<CategoryOption>('Life');
    const [location, setLocation] = useState('');
    const [publishedAt, setPublishedAt] = useState(() => `${Math.floor(Date.now() / 1000)}`);
    const [includeInFeed, setIncludeInFeed] = useState(true);
    const [photos, setPhotos] = useState<PendingPhoto[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [resultMessage, setResultMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [submitTarget, setSubmitTarget] = useState<SubmitTarget>('beta');
    const [loadingFeaturedCollection, setLoadingFeaturedCollection] = useState(false);
    const [featuredCollectionError, setFeaturedCollectionError] = useState('');
    const [loadingExistingCollection, setLoadingExistingCollection] = useState(false);
    const [existingCollectionError, setExistingCollectionError] = useState('');
    const [draggedPhotoId, setDraggedPhotoId] = useState<string | null>(null);
    const [dragOverPhotoId, setDragOverPhotoId] = useState<string | null>(null);

    const totalUploadBytes = useMemo(
        () => photos.reduce((total, image) => total + (image.file?.size || 0), 0),
        [photos],
    );
    const estimatedRequestBytes = useMemo(() => {
        const base64InflationMultiplier = 4 / 3;
        const perPhotoMetadataBytes = 800;
        const staticRequestOverheadBytes = 3000;
        const estimatedBase64Bytes = totalUploadBytes * base64InflationMultiplier;
        return Math.ceil(
            estimatedBase64Bytes + photos.length * perPhotoMetadataBytes + staticRequestOverheadBytes,
        );
    }, [photos.length, totalUploadBytes]);
    const targetApiPath = submitTarget === 'prod' ? PROD_API_PATH : BETA_API_PATH;

    const mapExistingCollectionPhotos = (images: ExistingCollectionImage[]) => {
        const sortedImages = [...(images || [])].sort((left, right) => left.sortOrder - right.sortOrder);
        return sortedImages.map((image) => ({
            localId: image.id,
            source: 'existing' as const,
            fileName: image.mediaKey || image.id,
            existingMediaKey: image.mediaKey,
            caption: image.caption || '',
            previewUrl: image.imageUrl,
            focalPointX: image.focalPointX ?? 50,
            focalPointY: image.focalPointY ?? 50,
            squareFocalPointX: image.squareFocalPointX ?? image.focalPointX ?? 50,
            squareFocalPointY: image.squareFocalPointY ?? image.focalPointY ?? 50,
        }));
    };

    useEffect(() => {
        if (targetFeaturedCollection) {
            setIncludeInFeed(false);
        }
    }, [targetFeaturedCollection]);

    useEffect(() => {
        if (!targetFeaturedCollection) {
            setFeaturedCollectionError('');
            setLoadingFeaturedCollection(false);
            return;
        }

        let isCancelled = false;
        const fetchFeaturedCollection = async () => {
            setLoadingFeaturedCollection(true);
            setFeaturedCollectionError('');
            try {
                const response = await fetch(`${targetApiPath}/photos/${FEATURED_COLLECTION_ID}`);
                if (!response.ok) {
                    throw new Error('Could not load existing featured photos.');
                }
                const data: ExistingCollectionResponse = await response.json();
                if (isCancelled) {
                    return;
                }
                setPhotos(mapExistingCollectionPhotos(data.galleryImages || []));
                setDescription(data.description || '');
                if (typeof data.publishedAt === 'number' && data.publishedAt > 0) {
                    setPublishedAt(`${data.publishedAt}`);
                }
            } catch (error) {
                if (!isCancelled) {
                    setFeaturedCollectionError(
                        error instanceof Error ? error.message : 'Could not load existing featured photos.',
                    );
                }
            } finally {
                if (!isCancelled) {
                    setLoadingFeaturedCollection(false);
                }
            }
        };

        fetchFeaturedCollection();

        return () => {
            isCancelled = true;
        };
    }, [targetApiPath, targetFeaturedCollection]);

    const handleLoadExistingCollection = async () => {
        const resolvedCollectionId = collectionId.trim();
        if (!resolvedCollectionId) {
            setExistingCollectionError('Collection ID is required to load an existing collection.');
            return;
        }

        setExistingCollectionError('');
        setLoadingExistingCollection(true);
        try {
            const response = await fetch(`${targetApiPath}/photos/${resolvedCollectionId}`);
            if (!response.ok) {
                throw new Error(`Could not load collection '${resolvedCollectionId}'.`);
            }
            const data: ExistingCollectionResponse = await response.json();
            setCollectionId(data.id || resolvedCollectionId);
            setTitle(data.title || '');
            setDescription(data.description || '');
            setCategory(data.category || 'Life');
            setLocation(data.location || '');
            if (typeof data.publishedAt === 'number' && data.publishedAt > 0) {
                setPublishedAt(`${data.publishedAt}`);
            }
            setPhotos(mapExistingCollectionPhotos(data.galleryImages || []));
            setResultMessage(`Loaded collection '${data.id || resolvedCollectionId}' for editing.`);
            setErrorMessage('');
        } catch (error) {
            setExistingCollectionError(
                error instanceof Error ? error.message : 'Could not load the existing collection.',
            );
        } finally {
            setLoadingExistingCollection(false);
        }
    };

    const handlePhotoSelection = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        const newPhotos = selectedFiles.map((file) => ({
            localId:
                typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                    ? crypto.randomUUID()
                    : `${file.name}-${Date.now()}-${Math.random()}`,
            source: 'new' as const,
            file,
            fileName: file.name,
            caption: '',
            previewUrl: URL.createObjectURL(file),
            focalPointX: 50,
            focalPointY: 50,
            squareFocalPointX: 50,
            squareFocalPointY: 50,
        }));
        setPhotos((previous) => [...previous, ...newPhotos]);
    };

    const handleUpdateCaption = (index: number, caption: string) => {
        setPhotos((previous) =>
            previous.map((photo, photoIndex) => (photoIndex === index ? { ...photo, caption } : photo)),
        );
    };

    const handleRemovePhoto = (index: number) => {
        setPhotos((previous) => {
            const photoToRemove = previous[index];
            if (photoToRemove?.source === 'new' && photoToRemove.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(photoToRemove.previewUrl);
            }
            return previous.filter((_photo, photoIndex) => photoIndex !== index);
        });
    };

    const handleUpdateFocalPoint = (index: number, axis: 'x' | 'y', value: number) => {
        setPhotos((previous) =>
            previous.map((photo, photoIndex) => {
                if (photoIndex !== index) {
                    return photo;
                }
                if (axis === 'x') {
                    return { ...photo, focalPointX: value };
                }
                return { ...photo, focalPointY: value };
            }),
        );
    };

    const handleUpdateSquareFocalPoint = (index: number, axis: 'x' | 'y', value: number) => {
        setPhotos((previous) =>
            previous.map((photo, photoIndex) => {
                if (photoIndex !== index) {
                    return photo;
                }
                if (axis === 'x') {
                    return { ...photo, squareFocalPointX: value };
                }
                return { ...photo, squareFocalPointY: value };
            }),
        );
    };

    const handleDragStart = (photoId: string, event: DragEvent<HTMLElement>) => {
        setDraggedPhotoId(photoId);
        setDragOverPhotoId(photoId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', photoId);
    };

    const handleDragEnter = (photoId: string) => {
        if (!draggedPhotoId || photoId === draggedPhotoId) {
            return;
        }
        setDragOverPhotoId(photoId);
    };

    const handleDragEnd = () => {
        setDraggedPhotoId(null);
        setDragOverPhotoId(null);
    };

    const handleDrop = (targetPhotoId: string) => {
        if (!draggedPhotoId || draggedPhotoId === targetPhotoId) {
            handleDragEnd();
            return;
        }
        setPhotos((previous) => {
            const reordered = [...previous];
            const sourceIndex = reordered.findIndex((photo) => photo.localId === draggedPhotoId);
            const targetIndex = reordered.findIndex((photo) => photo.localId === targetPhotoId);
            if (sourceIndex < 0 || targetIndex < 0) {
                return previous;
            }
            const [moved] = reordered.splice(sourceIndex, 1);
            reordered.splice(targetIndex, 0, moved);
            return reordered;
        });
        handleDragEnd();
    };

    const handleSubmit = async () => {
        setResultMessage('');
        setErrorMessage('');
        const targetCollectionId = targetFeaturedCollection ? FEATURED_COLLECTION_ID : collectionId;
        const resolvedTitle = targetFeaturedCollection ? FEATURED_COLLECTION_DEFAULT_TITLE : title;
        const resolvedCategory = targetFeaturedCollection ? FEATURED_COLLECTION_DEFAULT_CATEGORY : category;
        const resolvedLocation = targetFeaturedCollection ? FEATURED_COLLECTION_DEFAULT_LOCATION : location;

        if (!targetFeaturedCollection && editExistingCollection && !targetCollectionId.trim()) {
            setErrorMessage('Collection ID is required when modifying an existing collection.');
            return;
        }
        if (!resolvedTitle.trim() || photos.length === 0) {
            setErrorMessage('Please provide a title and at least one photo.');
            return;
        }
        const parsedPublishedAt = Number.parseInt(publishedAt, 10);
        if (!Number.isInteger(parsedPublishedAt)) {
            setErrorMessage('Published timestamp must be a valid unix timestamp.');
            return;
        }

        setSubmitting(true);
        try {
            let resolvedCollectionId = targetCollectionId || '';
            let totalUploadedPhotos = 0;

            for (let photoIndex = 0; photoIndex < photos.length; photoIndex += 1) {
                const chunkPhotos = [photos[photoIndex]];
                const photoPayload: UploadPhotoPayload[] = await Promise.all(
                    chunkPhotos.map(async (photo) => {
                        const basePayload: UploadPhotoPayload = {
                            caption: photo.caption,
                            sortOrder: photoIndex + 1,
                            focalPointX: photo.focalPointX,
                            focalPointY: photo.focalPointY,
                            squareFocalPointX: targetFeaturedCollection ? photo.squareFocalPointX : undefined,
                            squareFocalPointY: targetFeaturedCollection ? photo.squareFocalPointY : undefined,
                        };
                        if (photo.source === 'existing') {
                            return {
                                ...basePayload,
                                mediaKey: photo.existingMediaKey,
                            };
                        }
                        return {
                            ...basePayload,
                            fileName: photo.file?.name || photo.fileName || 'image.jpg',
                            contentType: photo.file?.type || 'image/jpeg',
                            base64Data: photo.file ? await toBase64(photo.file) : undefined,
                        };
                    }),
                );
                const isFinalChunk = photoIndex === photos.length - 1;

                const response = await fetch(`${targetApiPath}/photos/submit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: resolvedTitle,
                        id: resolvedCollectionId || targetCollectionId || undefined,
                        description,
                        category: resolvedCategory,
                        location: resolvedLocation,
                        publishedAt: parsedPublishedAt,
                        includeInFeed: !targetFeaturedCollection && includeInFeed && isFinalChunk,
                        replaceExisting: photoIndex === 0,
                        photos: photoPayload,
                    }),
                });

                if (!response.ok) {
                    const errorBody = await response.text();
                    throw new Error(errorBody || 'Could not submit collection.');
                }

                const data = await response.json();
                resolvedCollectionId = data.collectionId || resolvedCollectionId;
                totalUploadedPhotos += Number(data.uploadedPhotoCount || 0);
            }

            setResultMessage(
                `Saved collection '${resolvedCollectionId || targetCollectionId}' to ${submitTarget} with ${totalUploadedPhotos} uploaded photos.`,
            );
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Could not submit collection.');
        } finally {
            setSubmitting(false);
        }
    };

    if (isProd()) {
        return (
            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
                <Typography sx={{ color: '#55729f' }}>Builder is only available in beta/local environments.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, md: 3 }, pb: { xs: 4, md: 6 } }}>
            <Typography variant="h3" sx={{ color: '#163f86', fontWeight: 700, mb: 1 }}>
                Photo Collection Builder
            </Typography>
            <Typography sx={{ color: '#55729f', mb: 2.5 }}>
                Dev-only uploader that creates collection/feed items and uploads photos to the selected environment.
            </Typography>

            <Stack spacing={1.5}>
                <TextField
                    select
                    label="Submit destination"
                    value={submitTarget}
                    onChange={(event) => setSubmitTarget(event.target.value as SubmitTarget)}
                >
                    <MenuItem value="beta">Beta</MenuItem>
                    <MenuItem value="prod">Prod</MenuItem>
                </TextField>
                {!targetFeaturedCollection && (
                    <TextField
                        label="Collection title"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={targetFeaturedCollection}
                            onChange={(event) => {
                                const shouldTargetFeatured = event.target.checked;
                                setTargetFeaturedCollection(shouldTargetFeatured);
                                if (shouldTargetFeatured) {
                                    setEditExistingCollection(false);
                                }
                                setExistingCollectionError('');
                            }}
                        />
                    }
                    label="Target featured photos collection"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={editExistingCollection}
                            disabled={targetFeaturedCollection}
                            onChange={(event) => {
                                const shouldEditExisting = event.target.checked;
                                setEditExistingCollection(shouldEditExisting);
                                if (shouldEditExisting) {
                                    setTargetFeaturedCollection(false);
                                }
                                setFeaturedCollectionError('');
                                setExistingCollectionError('');
                            }}
                        />
                    }
                    label="Modify existing collection by ID"
                />
                {targetFeaturedCollection && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Title is automatically set to &quot;{FEATURED_COLLECTION_DEFAULT_TITLE}&quot;.
                    </Typography>
                )}
                {editExistingCollection && !targetFeaturedCollection && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Load a collection ID to edit title, metadata, and image order/captions.
                    </Typography>
                )}
                {targetFeaturedCollection && loadingFeaturedCollection && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Loading existing featured photos...
                    </Typography>
                )}
                {targetFeaturedCollection && !loadingFeaturedCollection && !featuredCollectionError && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Existing featured photos are loaded below. Remove any to delete them when you submit.
                    </Typography>
                )}
                {targetFeaturedCollection && featuredCollectionError && (
                    <Typography sx={{ color: '#b3261e', fontSize: 13 }}>{featuredCollectionError}</Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                    <TextField
                        label={editExistingCollection ? 'Collection id to edit' : 'Collection id (optional slug)'}
                        value={targetFeaturedCollection ? FEATURED_COLLECTION_ID : collectionId}
                        onChange={(event) => setCollectionId(event.target.value)}
                        disabled={targetFeaturedCollection || loadingExistingCollection}
                        helperText={targetFeaturedCollection ? "Using fixed id 'featured'." : undefined}
                        fullWidth
                    />
                    {editExistingCollection && !targetFeaturedCollection && (
                        <Button
                            variant="outlined"
                            onClick={handleLoadExistingCollection}
                            disabled={loadingExistingCollection || !collectionId.trim()}
                        >
                            {loadingExistingCollection ? 'Loading...' : 'Load collection'}
                        </Button>
                    )}
                </Stack>
                {editExistingCollection && existingCollectionError && (
                    <Typography sx={{ color: '#b3261e', fontSize: 13 }}>{existingCollectionError}</Typography>
                )}
                <TextField
                    label="Description"
                    multiline
                    minRows={2}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />
                {!targetFeaturedCollection && (
                    <TextField
                        select
                        label="Category"
                        value={category}
                        onChange={(event) => setCategory(event.target.value as CategoryOption)}
                    >
                        {CATEGORY_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                {!targetFeaturedCollection && (
                    <TextField label="Location" value={location} onChange={(event) => setLocation(event.target.value)} />
                )}
                {targetFeaturedCollection && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Category/location are set automatically for featured updates.
                    </Typography>
                )}
                <TextField
                    label="Published timestamp (unix seconds)"
                    value={publishedAt}
                    onChange={(event) => setPublishedAt(event.target.value)}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeInFeed}
                            onChange={(event) => setIncludeInFeed(event.target.checked)}
                            disabled={targetFeaturedCollection}
                        />
                    }
                    label="Add collection to feed"
                />

                <Button variant="outlined" component="label" startIcon={<UploadOutlinedIcon />}>
                    Add photos
                    <input type="file" hidden multiple accept="image/*" onChange={handlePhotoSelection} />
                </Button>
                <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                    Selected: {photos.length} photos ({(totalUploadBytes / (1024 * 1024)).toFixed(2)} MB)
                </Typography>
                <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                    Estimated request payload: {(estimatedRequestBytes / (1024 * 1024)).toFixed(2)} MB
                </Typography>
                {estimatedRequestBytes > MAX_ESTIMATED_REQUEST_BYTES && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Large upload detected. The builder will submit photos in smaller chunks automatically.
                    </Typography>
                )}
                {photos.length > 1 && (
                    <Typography sx={{ color: '#55729f', fontSize: 13 }}>
                        Drag and drop photos to set display order.
                    </Typography>
                )}

                <Stack spacing={1.2}>
                    {photos.map((photo, index) => (
                        <Box
                            key={photo.localId}
                            onDragEnter={() => handleDragEnter(photo.localId)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => handleDrop(photo.localId)}
                            onDragEnd={handleDragEnd}
                            sx={{
                                border: '1px solid #d9e7f8',
                                borderRadius: 2,
                                p: 1.2,
                                transition: 'border-color 120ms ease, background-color 120ms ease',
                                borderColor: dragOverPhotoId === photo.localId ? '#7da8e8' : '#d9e7f8',
                                backgroundColor: dragOverPhotoId === photo.localId ? '#f1f7ff' : 'transparent',
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
                                <Box
                                    draggable
                                    onDragStart={(event) => handleDragStart(photo.localId, event)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        color: '#55729f',
                                        cursor: 'grab',
                                    }}
                                >
                                    <DragIndicatorIcon fontSize="small" />
                                    <Typography sx={{ fontSize: 12, color: '#55729f' }}>#{index + 1}</Typography>
                                </Box>
                                <Box
                                    component="img"
                                    src={photo.previewUrl}
                                    alt={photo.fileName}
                                    sx={{
                                        width: 90,
                                        height: 70,
                                        objectFit: 'cover',
                                        borderRadius: 1.5,
                                        objectPosition: `${photo.focalPointX}% ${photo.focalPointY}%`,
                                    }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ color: '#163f86', fontWeight: 600, fontSize: 14 }}>
                                        {photo.fileName}
                                    </Typography>
                                    {photo.source === 'existing' && (
                                        <Typography sx={{ color: '#55729f', fontSize: 12 }}>
                                            Existing collection photo
                                        </Typography>
                                    )}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Caption (optional)"
                                        value={photo.caption}
                                        onChange={(event) => handleUpdateCaption(index, event.target.value)}
                                        sx={{ mt: 0.8 }}
                                    />
                                    <Box sx={{ mt: 1 }}>
                                        <Typography sx={{ color: '#55729f', fontSize: 12 }}>Focus X ({photo.focalPointX}%)</Typography>
                                        <Slider
                                            size="small"
                                            min={0}
                                            max={100}
                                            value={photo.focalPointX}
                                            onChange={(_event, value) => handleUpdateFocalPoint(index, 'x', value as number)}
                                        />
                                        <Typography sx={{ color: '#55729f', fontSize: 12, mt: 0.2 }}>
                                            Focus Y ({photo.focalPointY}%)
                                        </Typography>
                                        <Slider
                                            size="small"
                                            min={0}
                                            max={100}
                                            value={photo.focalPointY}
                                            onChange={(_event, value) => handleUpdateFocalPoint(index, 'y', value as number)}
                                        />
                                        {targetFeaturedCollection && (
                                            <>
                                                <Typography sx={{ color: '#55729f', fontSize: 12, mt: 0.2 }}>
                                                    Square focus X ({photo.squareFocalPointX}%)
                                                </Typography>
                                                <Slider
                                                    size="small"
                                                    min={0}
                                                    max={100}
                                                    value={photo.squareFocalPointX}
                                                    onChange={(_event, value) =>
                                                        handleUpdateSquareFocalPoint(index, 'x', value as number)
                                                    }
                                                />
                                                <Typography sx={{ color: '#55729f', fontSize: 12, mt: 0.2 }}>
                                                    Square focus Y ({photo.squareFocalPointY}%)
                                                </Typography>
                                                <Slider
                                                    size="small"
                                                    min={0}
                                                    max={100}
                                                    value={photo.squareFocalPointY}
                                                    onChange={(_event, value) =>
                                                        handleUpdateSquareFocalPoint(index, 'y', value as number)
                                                    }
                                                />
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                <Box sx={{ width: 200, flexShrink: 0 }}>
                                    <Typography sx={{ color: '#55729f', fontSize: 12, mb: 0.5 }}>Collection preview (16:10)</Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            aspectRatio: '16 / 10',
                                            borderRadius: 1.5,
                                            overflow: 'hidden',
                                            border: '1px solid #d9e7f8',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={photo.previewUrl}
                                                alt={`${photo.fileName} collection preview`}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                objectPosition: `${photo.focalPointX}% ${photo.focalPointY}%`,
                                                display: 'block',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                {targetFeaturedCollection && (
                                    <Box sx={{ width: 170, flexShrink: 0 }}>
                                        <Typography sx={{ color: '#55729f', fontSize: 12, mb: 0.5 }}>
                                            Featured preview (1:1)
                                        </Typography>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                aspectRatio: '1 / 1',
                                                borderRadius: 1.5,
                                                overflow: 'hidden',
                                                border: '1px solid #d9e7f8',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={photo.previewUrl}
                                                alt={`${photo.fileName} featured preview`}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    objectPosition: `${photo.squareFocalPointX}% ${photo.squareFocalPointY}%`,
                                                    display: 'block',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                                <Button color="error" onClick={() => handleRemovePhoto(index)}>
                                    Remove
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Stack>

                <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
                    {submitting
                        ? 'Submitting...'
                        : targetFeaturedCollection
                          ? `Save featured photos in ${submitTarget}`
                          : editExistingCollection
                            ? `Update collection in ${submitTarget}`
                            : `Create collection in ${submitTarget}`}
                </Button>
            </Stack>

            {resultMessage && <Typography sx={{ color: '#2d7a3e', mt: 2 }}>{resultMessage}</Typography>}
            {errorMessage && <Typography sx={{ color: '#b3261e', mt: 2 }}>{errorMessage}</Typography>}
        </Box>
    );
}
